/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  НОРМАЛИЗАЦИЯ ОШИБОК: что угодно → `ApiError`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Из `lib/http.ts` наружу не выходит ни одна не-`ApiError`. Сторы, композаблы
 * и вью ловят ровно один тип и ветвятся ровно по одному полю — `reason`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ГЛАВНОЕ ПРАВИЛО: `message` С ПРОВОДА ПОЛЬЗОВАТЕЛЮ НЕ ПОКАЗЫВАЕТСЯ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Конверт кратоса — `{code, reason, message, metadata}` (см. `types/api.ts`).
 * Его `message` — **английский текст для разработчика**: «failed to update
 * session: pq: duplicate key value violates unique constraint». Показать его
 * пользователю значит одновременно испортить UX и слить внутренности схемы.
 *
 * Поэтому `ApiError.message` (то, что уедет в тост) собирается **здесь**, из
 * `reason`, а серверный текст откладывается в `devMessage` — для консоли,
 * Sentry и багрепортов. Цепочка фолбэков:
 *
 *     спец-случай (`WEAK_PASSWORD.rule`, `RATE_LIMITED.retry_after_s`)
 *       → текст по `reason`
 *         → текст по HTTP-статусу      (сюда попадают ответы не от кратоса:
 *                                       502 от nginx, 500 от прокси Vite)
 *           → общий текст
 *
 * Словарь `reason → русский текст` покрывает все 25 значений `ErrorReason`,
 * три ненормализованных (`VALIDATOR`, `CODEC`, `UNAUTHORIZED` — они рождаются
 * в middleware кратоса выше нашего кода) и четыре чисто клиентских
 * (`NETWORK`, `TIMEOUT`, `CANCELED`, `NO_REFRESH_TOKEN`).
 */

import axios from 'axios'

import type { ErrorResponse, WeakPasswordRule } from '@/types/api'

/* ═════════════════════════════════════════════════════════════════════════
   1. ЛОКАЛЬНЫЕ `reason`, КОТОРЫХ НЕТ НА ПРОВОДЕ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Причины, которые придумывает клиент: сервер до них не доехал или не ответил.
 * Живут в том же пространстве `reason`, чтобы у вызывающего кода был **один**
 * `switch`, а не «сначала проверь, не сетевая ли это ошибка».
 *
 * • `NETWORK`          — ответа нет вообще (сервер лежит, DNS, обрыв, CORS-preflight).
 * • `TIMEOUT`          — не уложились в `timeout` axios.
 * • `CANCELED`         — `AbortController.abort()`; **не ошибка**, см. текст ниже.
 * • `NO_REFRESH_TOKEN` — refresh нечем делать: в хранилище пусто.
 */
export const LOCAL_ERROR_REASONS = ['NETWORK', 'TIMEOUT', 'CANCELED', 'NO_REFRESH_TOKEN'] as const
export type LocalErrorReason = typeof LOCAL_ERROR_REASONS[number]

/* ═════════════════════════════════════════════════════════════════════════
   2. СЛОВАРИ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * `reason` → текст для пользователя.
 *
 * Карта намеренно `Record<string, string>`, а не `Record<ErrorReason, string>`:
 * фронт старше или новее бэкенда ровно на один деплой, и неизвестный ключ
 * обязан давать общий текст, а не `undefined` в интерфейсе (см. комментарий
 * к `UNNORMALIZED_ERROR_REASONS` в `types/api.ts`). Тотальность здесь
 * обеспечивается фолбэком, а не типом.
 */
const REASON_TEXT: Record<string, string> = {
  /* — 25 значений контракта `scanner.admin.v1.ErrorReason` — */
  UNKNOWN: 'Внутренняя ошибка сервера. Попробуйте позже.',
  VALIDATION_FAILED: 'Проверьте правильность заполнения полей.',
  INVALID_CREDENTIALS: 'Неверный логин или пароль.',
  ACCESS_TOKEN_MISSING: 'Требуется вход в систему.',
  ACCESS_TOKEN_INVALID: 'Сессия недействительна. Войдите заново.',
  ACCESS_TOKEN_EXPIRED: 'Сессия истекла. Войдите заново.',
  REFRESH_TOKEN_INVALID: 'Сессия недействительна. Войдите заново.',
  REFRESH_TOKEN_EXPIRED: 'Срок сессии истёк. Войдите заново.',
  REFRESH_TOKEN_REUSED: 'Сессия завершена: токен доступа был использован повторно. Войдите заново.',
  SESSION_REVOKED: 'Сессия завершена. Войдите заново.',
  SESSION_NOT_FOUND: 'Сессия не найдена.',
  FORBIDDEN: 'Недостаточно прав для этого действия.',
  USER_NOT_FOUND: 'Пользователь не найден.',
  EMAIL_ALREADY_EXISTS: 'Пользователь с таким email уже существует.',
  USERNAME_ALREADY_EXISTS: 'Пользователь с таким логином уже существует.',
  USER_DISABLED: 'Учётная запись отключена. Обратитесь к администратору.',
  WRONG_CURRENT_PASSWORD: 'Текущий пароль указан неверно.',
  WEAK_PASSWORD: 'Пароль не соответствует требованиям безопасности.',
  CANNOT_MODIFY_SELF: 'Нельзя выполнить это действие над собственной учётной записью.',
  LAST_ADMIN: 'Это последний активный администратор — его нельзя понизить, отключить или удалить.',
  RATE_LIMITED: 'Слишком много попыток. Попробуйте позже.',
  PARTICIPANT_NOT_FOUND: 'Участник не найден.',
  INVALID_QR_PAYLOAD: 'QR-код не распознан как код участника.',
  IMPORT_FILE_INVALID: 'Файл не удалось разобрать. Проверьте формат и колонки.',
  IMPORT_FILE_TOO_LARGE: 'Файл слишком большой. Максимум 5 МБ.',

  /* — не нормализованные бэкендом: middleware кратоса выше нашего кода — */
  VALIDATOR: 'Проверьте правильность заполнения полей.',
  CODEC: 'Некорректный формат запроса.',
  UNAUTHORIZED: 'Требуется вход в систему.',

  /* — чисто клиентские — */
  NETWORK: 'Сервер недоступен. Проверьте соединение и попробуйте ещё раз.',
  TIMEOUT: 'Сервер не ответил вовремя. Попробуйте ещё раз.',
  NO_REFRESH_TOKEN: 'Сессия не найдена. Войдите заново.',

  /**
   * Пустая строка — не забытый перевод, а осознанное решение.
   *
   * Отменённый запрос (`AbortController.abort()`) — штатная часть поиска
   * по мере набора: каждое нажатие клавиши гасит предыдущий `GET /users`.
   * Показать здесь текст значит мигать красным тостом на каждую букву.
   * Вызывающий код проверяет `if (!error.message) return` — либо
   * `error.reason === 'CANCELED'`, если хочет ветвиться явно.
   */
  CANCELED: '',
}

/**
 * HTTP-статус → текст. Второй эшелон: сюда попадает всё, что **не** конверт
 * кратоса, — 502/504 от nginx, 500 от прокси Vite при мёртвом бэкенде,
 * страница-заглушка балансировщика.
 */
const STATUS_TEXT: Record<number, string> = {
  400: 'Некорректный запрос.',
  401: 'Требуется вход в систему.',
  403: 'Недостаточно прав для этого действия.',
  404: 'Запрошенные данные не найдены.',
  405: 'Операция не поддерживается.',
  409: 'Данные уже изменились. Обновите страницу и повторите.',
  413: 'Слишком большой запрос.',
  429: 'Слишком много запросов. Попробуйте позже.',
  500: 'Внутренняя ошибка сервера.',
  502: 'Сервер временно недоступен. Попробуйте позже.',
  503: 'Сервер временно недоступен. Попробуйте позже.',
  504: 'Сервер не ответил вовремя. Попробуйте ещё раз.',
}

const GENERIC_TEXT = 'Что-то пошло не так. Попробуйте ещё раз.'

/**
 * `WEAK_PASSWORD.metadata.rule` → конкретное предложение.
 *
 * Смысл в том, чтобы не показывать «пароль слишком слабый» и заставлять
 * угадывать: сервер прислал, какое именно правило нарушено (семь значений,
 * см. `WEAK_PASSWORD_RULES` в `types/api.ts`), — грех этим не воспользоваться.
 * Числа продублированы из серверной политики (8–128, §1.4 плана) осознанно:
 * это текст для человека, а не источник правды для валидатора.
 */
const WEAK_PASSWORD_TEXT: Record<WeakPasswordRule, string> = {
  min_length: 'Пароль должен содержать не менее 8 символов.',
  max_length: 'Пароль должен содержать не более 128 символов.',
  needs_lower: 'Пароль должен содержать хотя бы одну строчную букву.',
  needs_upper: 'Пароль должен содержать хотя бы одну заглавную букву.',
  needs_digit: 'Пароль должен содержать хотя бы одну цифру.',
  contains_login: 'Пароль не должен содержать email или имя пользователя.',
  control_chars: 'Пароль не должен содержать управляющие символы.',
}

/**
 * `reason`, которые вешаются на **поле формы**, а не на тост.
 *
 * Ровно по этому признаку `useServerErrors` (F3) решает, звать ли
 * `setErrors()` во vee-validate. Обратите внимание, что `RATE_LIMITED`
 * сюда не входит: он про попытку, а не про введённые данные.
 */
const VALIDATION_REASONS: ReadonlySet<string> = new Set([
  'VALIDATION_FAILED',
  'VALIDATOR',
  'WEAK_PASSWORD',
  'WRONG_CURRENT_PASSWORD',
  'EMAIL_ALREADY_EXISTS',
  'USERNAME_ALREADY_EXISTS',
])

/* ═════════════════════════════════════════════════════════════════════════
   3. СБОРКА ТЕКСТА
   ═════════════════════════════════════════════════════════════════════════ */

/** Секунды → «через 45 секунд» / «через 3 минуты». */
function formatRetryAfter(seconds: number): string {
  if (seconds < 60) return `Повторите через ${seconds} с.`
  const minutes = Math.ceil(seconds / 60)
  const noun = minutes % 10 === 1 && minutes % 100 !== 11 ? 'минуту' : 'минут'
  return `Повторите через ${minutes} ${noun}.`
}

/**
 * Спец-случаи, где `metadata` делает текст точнее. Возвращает `null`, если
 * уточнять нечем, — и тогда работает обычная цепочка фолбэков.
 */
function specificText(reason: string, metadata: Record<string, string>): string | null {
  if (reason === 'WEAK_PASSWORD') {
    const rule = metadata.rule as WeakPasswordRule | undefined
    return (rule && WEAK_PASSWORD_TEXT[rule]) || null
  }

  if (reason === 'RATE_LIMITED') {
    // Значения `metadata` — всегда строки, даже числовые (см. `types/api.ts`).
    const seconds = Number(metadata.retry_after_s)
    if (Number.isFinite(seconds) && seconds > 0) {
      return `${REASON_TEXT.RATE_LIMITED} ${formatRetryAfter(Math.ceil(seconds))}`
    }
    return null
  }

  return null
}

/** Полная цепочка: спец-случай → reason → статус → общий текст. */
function describe(reason: string, status: number, metadata: Record<string, string>): string {
  const specific = specificText(reason, metadata)
  if (specific !== null) return specific

  // `??`, а не `||`: у `CANCELED` текст — пустая строка, и она значащая.
  return REASON_TEXT[reason] ?? STATUS_TEXT[status] ?? GENERIC_TEXT
}

/* ═════════════════════════════════════════════════════════════════════════
   4. КЛАСС
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Единственный тип ошибки, который видит приложение.
 *
 * Поля присваиваются в теле конструктора, а не через параметры-свойства:
 * `erasableSyntaxOnly: true` в `tsconfig.app.json` запрещает конструкцию
 * `constructor(public status: number)` — у неё есть рантайм-семантика,
 * несводимая к стиранию типов.
 */
export class ApiError extends Error {
  /** HTTP-статус. `0` — ответа не было вообще (`NETWORK` / `TIMEOUT` / `CANCELED`). */
  status: number

  /** Ключ ветвления и локализации. Не обязан входить в `ErrorReason` — см. шапку. */
  reason: string

  /** `map<string,string>` из конверта. Всегда объект, в худшем случае пустой. */
  metadata: Record<string, string>

  /**
   * Серверный `message` — **английский, для разработчика**.
   * Для логов и багрепортов; в интерфейс не попадает никогда.
   */
  devMessage: string

  constructor(
    status: number,
    reason: string,
    metadata: Record<string, string> = {},
    devMessage = '',
  ) {
    super(describe(reason, status, metadata))

    this.name = 'ApiError'
    this.status = status
    this.reason = reason
    this.metadata = metadata
    this.devMessage = devMessage
  }

  /**
   * Ошибка относится к введённым данным ⇒ её место под полем формы,
   * а не в тосте (`useServerErrors` вешает её через `setErrors()`).
   */
  get isValidation(): boolean {
    return VALIDATION_REASONS.has(this.reason)
  }

  /**
   * Запрос отменён вызывающим кодом. Не ошибка: `message` пустой,
   * показывать нечего. Вынесено в геттер, чтобы вызывающий код не
   * сравнивал строки руками.
   */
  get isCanceled(): boolean {
    return this.reason === 'CANCELED'
  }
}

/* ═════════════════════════════════════════════════════════════════════════
   5. НОРМАЛИЗАЦИЯ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Похоже ли `data` на конверт ошибки кратоса.
 *
 * Проверяем именно `reason`, а не `code`: `code` есть и у половины чужих
 * JSON-ответов, а `reason: string` — подпись конкретно нашего конверта.
 */
function isErrorEnvelope(data: unknown): data is Partial<ErrorResponse> {
  return typeof data === 'object' && data !== null && typeof (data as ErrorResponse).reason === 'string'
}

/**
 * `metadata` в конверте — protobuf `map`, то есть по контракту присутствует
 * всегда (`{}`, когда пусто), и `err.metadata.email` читается без `?.`.
 *
 * Здесь мы всё равно приводим её к объекту: этот код обрабатывает и ответы,
 * которые кратос не писал (502 от nginx, JSON-заглушка балансировщика), и
 * ломаться на них он не имеет права.
 */
function toMetadata(value: unknown): Record<string, string> {
  if (typeof value !== 'object' || value === null) return {}

  const result: Record<string, string> = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (typeof item === 'string') result[key] = item
    else if (typeof item === 'number' || typeof item === 'boolean') result[key] = String(item)
  }
  return result
}

/**
 * Что угодно → `ApiError`. Идемпотентна: `normalizeError(apiError) === apiError`.
 *
 * Порядок веток — от самого специфичного к самому общему, и он значим:
 * у отменённого запроса `response` тоже отсутствует, так что проверка
 * `ERR_CANCELED` обязана идти **до** проверки на сетевую ошибку, иначе
 * каждое нажатие клавиши в поиске давало бы «Сервер недоступен».
 */
export function normalizeError(err: unknown): ApiError {
  if (err instanceof ApiError) return err

  if (axios.isAxiosError(err)) {
    // 1. Отмена — не ошибка. Должна идти первой: `response` у неё тоже нет.
    if (err.code === 'ERR_CANCELED') {
      return new ApiError(0, 'CANCELED', {}, err.message)
    }

    // 2. Не уложились в `timeout`. `ETIMEDOUT` — тот же случай под Node
    //    (MSW и тесты ходят через http-адаптер, а не через XHR).
    if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
      return new ApiError(0, 'TIMEOUT', {}, err.message)
    }

    const response = err.response

    // 3. Ответа не было вообще: сервер лежит, обрыв, DNS, зарубленный preflight.
    if (!response) {
      return new ApiError(0, 'NETWORK', {}, err.message)
    }

    // 4. Ответ есть. Конверт кратоса — если это он.
    const data: unknown = response.data
    if (isErrorEnvelope(data)) {
      return new ApiError(
        // `code` в конверте равен HTTP-статусу, но авторитет — сам статус:
        // на пути к браузеру ответ мог переписать прокси.
        response.status,
        data.reason ?? 'UNKNOWN',
        toMetadata(data.metadata),
        typeof data.message === 'string' ? data.message : '',
      )
    }

    // 5. Ответ не от кратоса. `reason` пустой — намеренно: сказать «UNKNOWN»
    //    значило бы приписать серверу причину, которой он не называл, и
    //    перебить более точный текст по статусу (502 → «Сервер временно
    //    недоступен» вместо «Внутренняя ошибка сервера»).
    return new ApiError(response.status, '', {}, typeof data === 'string' ? data : err.message)
  }

  // 6. Не axios: `AbortError` от голого fetch, TypeError из нашего же кода, что угодно.
  if (err instanceof DOMException && err.name === 'AbortError') {
    return new ApiError(0, 'CANCELED', {}, err.message)
  }

  if (err instanceof Error) {
    return new ApiError(0, 'UNKNOWN', {}, err.message)
  }

  return new ApiError(0, 'UNKNOWN', {}, String(err))
}
